import { create } from "zustand";
import type { TEDNotice } from "../../../engine/tedClient";
import type { RedFlagResult } from "../../../engine/tenderRedFlags";
import { API } from "@/lib/apiConfig";

const TENDER_BASE = API.tender;

/**
 * Global Tender Integrity Mesh Store (v2)
 *
 * Manages global tender integrity mesh, vendor counts, conflict flags,
 * transparency scoring, and procurement risk calculations.
 */
export interface TenderMeshStoreState {
  vendorCount: number;
  conflictOfInterestFlags: number;
  transparencyScore: number; // 0-100
  meshLatencyMS: number;
  procurementRiskScore: number; // 0-100
  corruptionSignals: number;
  geopoliticalWeight: number; // 0-5

  liveTenders: TEDNotice[];
  lastSyncAt: string | null;
  syncInProgress: boolean;
  redFlagsByPubNum: Record<string, RedFlagResult>;

  // Actions
  setVendorCount: (count: number) => void;
  setConflictOfInterestFlags: (flags: number) => void;
  setTransparencyScore: (score: number) => void;
  setCorruptionSignals: (signals: number) => void;
  setGeopoliticalWeight: (weight: number) => void;
  simulateMeshLatency: () => void;
  syncTenders: (country: string, cpv: string, since: string) => Promise<void>;
  loadCachedTenders: (country: string, since: string) => Promise<void>;
  analyzeRedFlags: (publicationNumber: string) => Promise<void>;

  // Derived outputs
  getConflictPenalty: () => number;
  getRiskPropagation: () => number;
  getMeshIntegrity: () => number;
  getProcurementRiskScore: () => number;
  getEscalationLevel: () => "low" | "medium" | "high";

  // Export
  exportTenderMesh: () => void;
}

export const useTenderMeshStore = create<TenderMeshStoreState>((set, get) => {
  // Initial procurement risk score calculation
  const initialMeshIntegrity = 100 - (0 * 3) - (0 / 20); // transparencyScore - conflictPenalty - latency
  const initialRiskPropagation = 0 * 5 + 0 * 2; // corruptionSignals * 5 + geopoliticalWeight * 2
  const initialRiskScore = Math.max(0, Math.min(100, 100 - initialMeshIntegrity - initialRiskPropagation));

  return {
  vendorCount: 0,
  conflictOfInterestFlags: 0,
  transparencyScore: 100,
  meshLatencyMS: 0,
  procurementRiskScore: initialRiskScore,
  corruptionSignals: 0,
  geopoliticalWeight: 0,
  liveTenders: [],
  lastSyncAt: null,
  syncInProgress: false,
  redFlagsByPubNum: {},

  setVendorCount: (count) => set({ vendorCount: Math.max(0, count) }),
  setConflictOfInterestFlags: (flags) => set({ conflictOfInterestFlags: Math.max(0, flags) }),
  setTransparencyScore: (score) => set({ transparencyScore: Math.max(0, Math.min(100, score)) }),
  setCorruptionSignals: (signals) => set({ corruptionSignals: Math.max(0, signals) }),
  setGeopoliticalWeight: (weight) => set({ geopoliticalWeight: Math.max(0, Math.min(5, weight)) }),

  syncTenders: async (country, cpv, since) => {
    set({ syncInProgress: true });
    const u = new URL("/tender/sync", TENDER_BASE);
    u.searchParams.set("country", country);
    u.searchParams.set("cpv", cpv);
    u.searchParams.set("since", since);
    const res = await fetch(u.toString());
    if (!res.ok) {
      set({ syncInProgress: false });
      throw new Error(`sync_failed_${res.status}`);
    }
    const j = (await res.json()) as { lastSync: string; count: number };
    set({ lastSyncAt: j.lastSync, syncInProgress: false });
    await get().loadCachedTenders(country, since);
  },

  loadCachedTenders: async (country, since) => {
    const u = new URL("/tender/list", TENDER_BASE);
    u.searchParams.set("country", country);
    u.searchParams.set("since", since);
    const res = await fetch(u.toString());
    if (!res.ok) {
      throw new Error(`list_failed_${res.status}`);
    }
    const j = (await res.json()) as { items: TEDNotice[] };
    set({ liveTenders: j.items || [] });
  },

  analyzeRedFlags: async (publicationNumber) => {
    const u = new URL(
      `/tender/${encodeURIComponent(publicationNumber)}/redflags`,
      TENDER_BASE,
    );
    const res = await fetch(u.toString());
    if (!res.ok) {
      throw new Error(`redflags_failed_${res.status}`);
    }
    const j = (await res.json()) as RedFlagResult;
    set((s) => ({
      redFlagsByPubNum: { ...s.redFlagsByPubNum, [publicationNumber]: j },
    }));
  },

  simulateMeshLatency: () => {
    const latency = Math.floor(Math.random() * 551) + 50; // 50-600ms
    set({ meshLatencyMS: latency });
    // Auto-recompute procurement risk score
    get().getProcurementRiskScore();
  },

  getConflictPenalty: () => {
    return get().conflictOfInterestFlags * 3;
  },

  getRiskPropagation: () => {
    const state = get();
    return state.corruptionSignals * 5 + state.geopoliticalWeight * 2;
  },

  getMeshIntegrity: () => {
    const state = get();
    const conflictPenalty = state.getConflictPenalty();
    return state.transparencyScore - conflictPenalty - (state.meshLatencyMS / 20);
  },

  getProcurementRiskScore: () => {
    const state = get();
    const meshIntegrity = state.getMeshIntegrity();
    const riskPropagation = state.getRiskPropagation();
    const score = 100 - meshIntegrity - riskPropagation;
    const riskScore = Math.max(0, Math.min(100, score));
    // Update state only if value changed
    if (state.procurementRiskScore !== riskScore) {
      set({ procurementRiskScore: riskScore });
    }
    return riskScore;
  },

  getEscalationLevel: () => {
    const state = get();
    const riskScore = state.getProcurementRiskScore();
    if (riskScore >= 60) return "high";
    if (riskScore >= 30) return "medium";
    return "low";
  },

  exportTenderMesh: () => {
    const state = get();
    const report = {
      vendorCount: state.vendorCount,
      transparencyScore: state.transparencyScore,
      meshLatencyMS: state.meshLatencyMS,
      procurementRiskScore: state.getProcurementRiskScore(),
      corruptionSignals: state.corruptionSignals,
      conflictOfInterestFlags: state.conflictOfInterestFlags,
      escalationLevel: state.getEscalationLevel(),
      geopoliticalWeight: state.geopoliticalWeight,
      conflictPenalty: state.getConflictPenalty(),
      riskPropagation: state.getRiskPropagation(),
      meshIntegrity: state.getMeshIntegrity(),
    };

    const dateStr = new Date().toISOString().split("T")[0]!.replace(/-/g, "");
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tenderMesh_${dateStr}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },
  };
});
